import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { WalletBalanceBadge } from '@root/entities/wallet/ui/desktop'
import {
  TradingControlsAmountSell,
  TradingControlsButtonBase,
} from '@root/features/trade/trading-controls'
import { TradingControlsQuote } from '@root/features/trade/trading-controls/ui/common/trading-controls-quote/trading-controls-quote'
import { TradingGas } from '@root/features/trade/trading-controls/ui/desktop'
import { AnimateHeight } from '@root/shared/ui-kit'
import { Dialog } from '@root/shared/ui-kit/dialog'
import { WalletsDialog } from '@root/widgets/desktop/wallets-dialog'
import { TradeSettingsModal } from '@root/widgets/trade/trade-settings/desktop'

import { reatomQuickSellModal } from '../../atoms/reatom-quick-sell'

import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { DialogProps } from '@root/shared/ui-kit/dialog'

type QuickSellModalProps = {
  position: AggregatedPositionModel | WalletPositionModel
} & Pick<DialogProps, 'onOpenChange' | 'open'>

export const QuickSellModal: React.FC<QuickSellModalProps> = ({
  open,
  onOpenChange,
  position,
}) => {
  const { t } = useTranslation()
  const { pair } = position

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomQuickSellModal({ position }),
    [position],
  )
  const [requestState] = useAtom(model.executeSwapModel)
  const [amount] = useAtom(model.inputModel.amountAtom)
  const [wallet, setWallet] = useAtom(model.walletAtom)
  const [walletsOpen, setWalletsOpen] = useState(false)
  const [quoteState] = useAtom(model.quoteModel.quoteStateAtom)
  const [validation] = useAtom(model.validationAtom)
  const executeSwap = useAction(model.executeSwapModel.execute)
  const [tradeSettings] = useAtom(model.tradeSettingsAtom)
  const [tradeSettingsOpen, setTradeSettingsOpen] = useState(false)
  useEffect(() => {
    if (requestState.type === 'Success') {
      onOpenChange?.(false)
    }
  }, [onOpenChange, requestState])

  const retryQuote = useAction(model.quoteModel.forceRetry)

  const quoteOutputAmountBase = quoteState.value?.outputAmount

  const title = `${t('trading.sell')} ${pair.quote.ticker}`

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        describedBy={t('trading.quick_trade')}
      >
        <Content>
          <Dialog.Header title={title} />
          <Form>
            <Settings>
              <Wallet>
                {wallet ? (
                  <WalletBalanceBadge
                    wallet={wallet}
                    currency={pair.quote}
                    onClick={() => {
                      setWalletsOpen(true)
                    }}
                  />
                ) : undefined}
              </Wallet>
              <TradingGas
                mevProtection="icon"
                onSettingsOpen={() => {
                  setTradeSettingsOpen(true)
                }}
                tradeSettings={tradeSettings}
              />
            </Settings>
            <TradingControlsAmountSell model={model.inputModel} pair={pair} />
            <AnimateHeight>
              {amount.isZero() ? undefined : (
                <QuoteStyled
                  swapDirection="sell"
                  pair={pair}
                  quoteState={quoteState}
                />
              )}
            </AnimateHeight>
            <SellButton
              variant="sell"
              onClick={executeSwap}
              requestState={requestState}
              validation={validation}
              pair={pair}
              amount={amount}
              baseAmount={quoteOutputAmountBase}
              currencyAmount={amount}
              quoteState={quoteState}
              onQuoteRetry={retryQuote}
            />
          </Form>
          <TradeSettingsModal
            open={tradeSettingsOpen}
            onOpenChange={setTradeSettingsOpen}
          />
        </Content>
      </Dialog>
      <AnimatePresence>
        {wallet ? (
          <WalletsDialog
            open={walletsOpen}
            onOpenChange={setWalletsOpen}
            selectedWalletId={wallet.id}
            currency={pair.quote}
            onWalletSelected={(wallet) => {
              setWallet(wallet)
              setWalletsOpen(false)
            }}
          />
        ) : undefined}
      </AnimatePresence>
    </>
  )
}

const Content = styled.div``

const Form = styled.div`
  display: grid;
  padding-block: 12px 0;

  gap: 12px;
  margin-bottom: 10px;
`

const Settings = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Wallet = styled.div`
  padding: 0 0 0 12px;
`

const SellButton = styled(TradingControlsButtonBase)`
  margin-inline: 10px;
`

const QuoteStyled = styled(TradingControlsQuote)`
  place-content: center;
`
